import { useState } from "react";
import { Images, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Img } from "@/components/common/Primitives";
import { Stepper } from "./GuestPicker";
import { useLang } from "@/lib/i18n";
import { nok, nightsLabel } from "@/lib/format";
import { BADGE_LABELS } from "@/lib/booking";

const Scarcity = ({ available }) => {
  const { tx } = useLang();
  if (available <= 0) return <span className="text-sm font-medium text-muted-foreground" data-testid="room-card-soldout">{tx("Fullt desse datoane", "Sold out for these dates")}</span>;
  if (available <= 3) return <span className="text-sm font-medium text-warning" data-testid="room-card-scarcity">{tx(`Berre ${available} att`, `Only ${available} left`)}</span>;
  return <span className="text-sm font-medium text-success" data-testid="room-card-available">{tx("Ledig", "Available")}</span>;
};

const QuickView = ({ rt, open, onOpenChange }) => {
  const { lang, tx, tr } = useLang();
  const [idx, setIdx] = useState(0);
  const imgs = rt.images || [];
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl gap-0 overflow-hidden rounded-2xl border-border bg-card p-0" data-testid={`room-quickview-${rt.id}`}>
        <div className="grid grid-cols-1 md:grid-cols-[1.1fr_1fr]">
          <div className="bg-secondary">
            <Img src={imgs[idx]} alt={tr(rt.name)} className="aspect-[4/3] md:aspect-auto md:h-full md:min-h-[420px]" radius="rounded-none" reveal={false} />
            {imgs.length > 1 && (
              <div className="flex gap-2 p-3">
                {imgs.map((s, i) => (
                  <button key={s} type="button" onClick={() => setIdx(i)} className={`h-12 w-16 overflow-hidden rounded-md border-2 ${i === idx ? "border-foreground" : "border-transparent opacity-80 hover:opacity-100"}`} aria-label={`${i + 1}`}>
                    <img src={s} alt="" className="h-full w-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </div>
          <div className="p-6 sm:p-8">
            <DialogTitle className="text-2xl font-semibold tracking-tight text-foreground">{tr(rt.name)}</DialogTitle>
            <p className="mt-1 text-sm text-muted-foreground">{tr(rt.tagline)}</p>
            <DialogDescription className="mt-4 text-[0.9375rem] leading-relaxed text-foreground/85">{tr(rt.description)}</DialogDescription>
            <ul className="mt-5 space-y-2">
              {(rt.highlights?.[lang] || rt.highlights?.nn || []).map((h) => <li key={h} className="flex gap-2 text-sm text-foreground"><Check className="mt-0.5 h-4 w-4 shrink-0 text-success" />{h}</li>)}
            </ul>
            <dl className="mt-6 grid grid-cols-2 gap-x-6 gap-y-3 border-t border-border pt-5 text-sm">
              <div><dt className="text-muted-foreground">{tx("Midtveke 1 / 2 pers.", "Weekday 1 / 2 guests")}</dt><dd className="font-semibold tabular text-foreground">{nok(rt.prices.weekday.p1, lang)} / {nok(rt.prices.weekday.p2, lang)}</dd></div>
              <div><dt className="text-muted-foreground">{tx("Helg 1 / 2 pers.", "Weekend 1 / 2 guests")}</dt><dd className="font-semibold tabular text-foreground">{nok(rt.prices.weekend.p1, lang)} / {nok(rt.prices.weekend.p2, lang)}</dd></div>
              {rt.max_extra_beds > 0 && <div><dt className="text-muted-foreground">{tx("Ekstraseng", "Extra bed")}</dt><dd className="font-semibold tabular text-foreground">+{nok(rt.extra_bed_price, lang)}/{tx("natt", "night")}</dd></div>}
              <div><dt className="text-muted-foreground">{tx("Frukost", "Breakfast")}</dt><dd className="font-semibold text-foreground">{tx("Inkludert", "Included")}</dd></div>
            </dl>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

/**
 * Room row for step 2: photo left, details right, price + add control bottom-right.
 */
export const BookingRoomCard = ({ rt, av, party, nights, qty, onQty, recommended, building }) => {
  const { lang, tx, tr } = useLang();
  const [qv, setQv] = useState(false);
  const available = av?.available ?? 0;
  const g = Math.max(1, Math.min(party, rt.max_guests));
  const q = av?.quotes?.[String(g)];
  const sold = available <= 0;
  const fits = rt.max_guests >= party;
  const selected = qty > 0;
  const highlights = (rt.highlights?.[lang] || rt.highlights?.nn || []).slice(0, 3);
  const meta = [building ? tr(building) : null, tx(`Inntil ${rt.max_guests} ${rt.max_guests === 1 ? "gjest" : "gjester"}`, `Up to ${rt.max_guests} ${rt.max_guests === 1 ? "guest" : "guests"}`), ...(rt.badges || []).map((b) => BADGE_LABELS[b] && tr(BADGE_LABELS[b]))].filter(Boolean);
  return (
    <article
      className={`overflow-hidden rounded-2xl border bg-card transition-[border-color,box-shadow] duration-200 ${selected ? "border-foreground shadow-float" : "border-border hover:border-foreground/50"} ${sold ? "opacity-60" : ""}`}
      data-testid={`room-card-${rt.id}`}
    >
      <div className="grid grid-cols-1 sm:grid-cols-[240px_1fr] lg:grid-cols-[280px_1fr]">
        <button type="button" onClick={() => setQv(true)} className="group relative block text-left" aria-label={tx("Sjå bilete og detaljar", "View photos & details")} data-testid={`room-card-${rt.id}-image`}>
          <div className="relative aspect-[4/3] overflow-hidden sm:aspect-auto sm:h-full sm:min-h-[220px]">
            <img src={rt.images?.[0]} alt={tr(rt.name)} loading="lazy" className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-[1.03]" />
            <span className="absolute bottom-3 left-3 inline-flex items-center gap-1.5 rounded-md bg-card/95 px-2.5 py-1 text-xs font-semibold text-foreground shadow-sm"><Images className="h-3.5 w-3.5" /> {rt.images?.length || 1}</span>
            {recommended && !selected && <span className="absolute left-3 top-3 rounded-md bg-card px-2.5 py-1 text-xs font-semibold text-foreground shadow-sm">{tx("Passar dykk", "Fits your party")}</span>}
            {selected && <span className="absolute right-3 top-3 inline-flex h-8 min-w-8 items-center justify-center rounded-full bg-foreground px-2.5 text-sm font-semibold text-background tabular" data-testid={`room-card-${rt.id}-qty-badge`}>{qty}×</span>}
          </div>
        </button>
        <div className="flex flex-col p-5 sm:p-6">
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0">
              <h3 className="text-lg font-semibold leading-snug tracking-tight text-foreground">{tr(rt.name)}</h3>
              <p className="mt-1 text-sm text-muted-foreground">{meta.join(" · ")}</p>
            </div>
            <div className="shrink-0 pt-0.5"><Scarcity available={available} /></div>
          </div>
          {highlights.length > 0 && (
            <ul className="mt-3 space-y-1">
              {highlights.map((h) => <li key={h} className="flex gap-2 text-sm text-foreground/85"><Check className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />{h}</li>)}
            </ul>
          )}
          {!fits && <p className="mt-3 text-sm text-muted-foreground">{tx("Kombiner med eit rom til for heile reisefølgjet.", "Combine with another room for the whole party.")}</p>}
          <div className="mt-auto flex flex-wrap items-end justify-between gap-4 pt-5">
            <div>
              {q ? (
                <>
                  <p className="text-xl font-semibold leading-none tabular text-foreground" data-testid={`room-card-${rt.id}-price`}>{nok(q.subtotal, lang)}</p>
                  <p className="mt-1.5 text-sm text-muted-foreground">{nightsLabel(nights, lang)} · {g} {g === 1 ? tx("person", "guest") : tx("personar", "guests")} · {nok(q.avg_per_night, lang)}/{tx("natt", "night")}</p>
                </>
              ) : <p className="text-sm text-muted-foreground">–</p>}
            </div>
            <div className="flex shrink-0 items-center gap-3">
              <button type="button" onClick={() => setQv(true)} className="text-sm font-semibold text-foreground underline underline-offset-4" data-testid={`room-card-${rt.id}-details`}>{tx("Detaljar", "Details")}</button>
              {sold ? (
                <Button variant="soft" disabled data-testid={`room-card-${rt.id}-soldout-button`}>{tx("Fullt", "Sold out")}</Button>
              ) : selected ? (
                <Stepper value={qty} min={0} max={available} onChange={onQty} label={tr(rt.name)} testid={`room-card-${rt.id}-quantity-stepper`} size="sm" />
              ) : (
                <Button onClick={() => onQty(1)} data-testid={`room-card-${rt.id}-add-button`}>{tx("Legg til", "Add")}</Button>
              )}
            </div>
          </div>
        </div>
      </div>
      {qv && <QuickView rt={rt} open={qv} onOpenChange={setQv} />}
    </article>
  );
};

/** Ready-made combinations covering the whole party */
export const SuggestionStrip = ({ combos, byId, nights, party, onPick, activeKey }) => {
  const { lang, tx, tr } = useLang();
  const labels = { value: ["Beste pris", "Best value"], sea: ["Med sjøutsikt", "With sea view"], fewest: ["Færrast rom", "Fewest rooms"] };
  const describe = (combo) => {
    const counts = {};
    combo.items.forEach((i) => { counts[i.room_type_id] = counts[i.room_type_id] || { n: 0, g: [] }; counts[i.room_type_id].n += 1; counts[i.room_type_id].g.push(i.guests); });
    return Object.entries(counts).map(([id, c]) => `${c.n}× ${tr(byId[id]?.name)} (${c.g.join("+")})`).join(" + ");
  };
  return (
    <section className="rounded-2xl bg-secondary p-5 sm:p-6" data-testid="booking-suggestions">
      <h3 className="text-base font-semibold text-foreground">{tx(`Forslag for ${party} gjester, ${nightsLabel(nights, lang)}`, `Suggestions for ${party} guests, ${nightsLabel(nights, lang)}`)}</h3>
      <p className="mt-1 text-sm text-muted-foreground">{tx("Ferdige kombinasjonar med plass til heile reisefølgjet. Vel eitt, eller sett saman sjølv under.", "Ready-made combinations with room for the whole party. Pick one, or build your own below.")}</p>
      <ul className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-3">
        {combos.map((c) => {
          const l = labels[c.key] || labels.value;
          const active = activeKey === c.key;
          return (
            <li key={c.key}>
              <button type="button" onClick={() => onPick(c)} data-testid={`booking-suggestion-${c.key}`} className={`flex h-full w-full flex-col rounded-xl border bg-card p-4 text-left transition-[border-color,box-shadow] ${active ? "border-foreground ring-1 ring-foreground" : "border-border hover:border-foreground/60"}`}>
                <span className="text-xs font-semibold text-muted-foreground">{tx(l[0], l[1])}</span>
                <span className="mt-1.5 text-sm leading-snug text-foreground">{describe(c)}</span>
                <span className="mt-3 text-base font-semibold tabular text-foreground">{nok(c.total, lang)}</span>
              </button>
            </li>
          );
        })}
      </ul>
    </section>
  );
};
